"use client";

import { useTranslations } from "next-intl";

import {
  comparableTeams,
  MIN_COMPARABLE_TEAMS,
  styleTagsFor,
  useTournamentTeamStats,
} from "@/entities/replay-metrics";
import { TeamStyleTagList } from "@/widgets/tournament-results/team-breakdown-dialog";

/** Ярлыки стиля осмыслены только в командном 5×5; в 1×1 и арене блока нет. */
const TEAM_MODES = new Set(["Classic", "Draft"]);

export interface MatchTeamStylesSide {
  name: string;
  /** Все идентификаторы, под которыми команда может прийти в метриках: сетки, ростера, драфта. */
  ids: Array<string | null | undefined>;
}

/**
 * KAN-770: стиль двух команд рядом в разборе матча.
 *
 * База сравнения та же, что в итогах турнира: сопоставимые команды ТУРНИРА, а не
 * две стороны матча — на двух командах медиана механически раздаёт ярлыки обеим.
 */
export function MatchTeamStyles({
  tournamentId,
  tournamentType,
  teamA,
  teamB,
}: {
  tournamentId: string;
  tournamentType: string | null;
  teamA: MatchTeamStylesSide;
  teamB: MatchTeamStylesSide;
}) {
  const t = useTranslations("TournamentStats.teamStats");
  const isTeamMode = TEAM_MODES.has(tournamentType ?? "");
  const { data } = useTournamentTeamStats(isTeamMode ? tournamentId : undefined);

  if (!isTeamMode || !data || data.length === 0) return null;

  const comparable = comparableTeams(data);
  const enough = comparable.length >= MIN_COMPARABLE_TEAMS;

  const column = (side: MatchTeamStylesSide) => {
    const row = comparable.find((r) => side.ids.includes(r.teamId));
    const tags = enough && row ? styleTagsFor(row, comparable) : [];
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="truncate text-sm font-medium text-foreground">{side.name}</div>
        {!enough || !row ? (
          <p className="text-xs text-muted-foreground">{t("styleFewData")}</p>
        ) : tags.length > 0 ? (
          <TeamStyleTagList tags={tags} />
        ) : (
          <p className="text-xs text-muted-foreground">{t("styleNeutral")}</p>
        )}
      </div>
    );
  };

  return (
    <section aria-labelledby="match-team-styles-title" className="space-y-3">
      <h2 id="match-team-styles-title" className="text-sm font-semibold text-foreground">
        {t("styleTitle")}
      </h2>
      <div className="grid grid-cols-2 gap-4">
        {column(teamA)}
        {column(teamB)}
      </div>
    </section>
  );
}
